import React, { useState, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Typography,
  Box,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import useStableCallback from "../../../hooks/useStableCallback";

const ContentSx = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  height: "100%",
  p: 2,
};

const HeaderSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  mb: 2,
};

const FormSx = {
  display: "inline-flex",
  maxWidth: "50%",
};

const FooterSx = {
  display: "inline-flex",
  marginTop: "20px",
};

const DividerSx = { my: 2 };

const ButtonSx = { mr: 1 };

const DatePickerProps = {
  sx: {
    color: (theme) => theme.palette.primary.main,
  },
};

const CustomDateTextField = styled(TextField)(() => ({
  ".MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "black",
  },
}));

const minValidDate = dayjs();

const maxValidDate = dayjs().add(6, "month");

const PONumberInputProps = {
  maxLength: 10,
};

const inputRegex = /^(?!.*[- ]{2})[A-Za-z0-9 ]*(?:[- &_][A-Za-z0-9 ]*){0,4}$/is;
const invalidRegex = /^[ -0]*$/;
const startEndSpecialCharRegex =
  /^[a-zA-Z0-9]+[a-zA-Z0-9&-_:.\- ]+[a-zA-Z0-9&-_:.\- ][a-zA-Z0-9]+$/;
const singleCharRegex =
  /^(?!.*[-]{2})(?=.*[a-zA-Z0-9]$)[a-zA-Z0-9][a-zA-Z0-9-]*$/;

const isValidPONumber = (value, setError) => {
  const currentValue = value.trim();
  if (!!currentValue) {
    if (
      currentValue.length <= 3 &&
      singleCharRegex.test(currentValue) &&
      !["0", "00", "000"].includes(currentValue)
    ) {
      setError({ error: false, helperText: "" });
      return true;
    }
    if (
      !currentValue.length ||
      !inputRegex.test(currentValue) ||
      invalidRegex.test(currentValue) ||
      !startEndSpecialCharRegex.test(currentValue)
    ) {
      setError({ error: true, helperText: "Invalid PO Number" });
      return false;
    }
    setError({ error: false, helperText: "" });
    return true;
  }
  setError({ error: true, helperText: "Please enter valid PO Number" });
  return false;
};

const selectionOptions = ["N", "Y"];

const INIT_FORM_DATA = {
  selected: "",
  datingDays: "",
  coreDiscount: "",
  nonCoreDiscount: "",
  discountMultiplier: "",
  qtyMultiplier: "",
};

export const GeneratePOByMap = [
  {
    id: 1,
    label: "Department",
  },
  {
    id: 2,
    label: "POG",
  },
  {
    id: 3,
    label: "Custom",
  },
];

function SettingsForm({
  onSettingsSave,
  onClose,
  onCreatePONumbers = () => {},
  onSaveDates = () => {},
  onDeleteDates = () => {},
  hasXPog = false,
  isXDepartment = false,
  selectedCount = 0,
}) {
  const [formData, setFormData] = useState(INIT_FORM_DATA);
  const [generatePOBy, setGeneratePOBy] = useState(GeneratePOByMap[0].id);
  const [customPONumber, setCustomPONumber] = useState("");
  const [inputError, setInputError] = useState({
    error: false,
    helperText: "",
  });
  const [dateValue, setDateValue] = useState(null);

  const handleGeneratePOByChange = useStableCallback((event) => {
    setInputError({ error: false, helperText: "" });
    setCustomPONumber("");
    setGeneratePOBy(event?.target?.value);
  });

  const handleCustomPONumberInput = useStableCallback((event) => {
    const value = event?.target?.value ?? "";
    const updatedValue = value.toUpperCase();
    isValidPONumber(updatedValue, setInputError);
    setCustomPONumber(updatedValue);
  });

  const CreatePONumberDisabled =
    inputError.error ||
    (!customPONumber.trim().length && generatePOBy === GeneratePOByMap[2].id);

  const handleCreatePONumbers = useStableCallback(() => {
    onCreatePONumbers({
      generatePOBy: GeneratePOByMap[generatePOBy - 1].label,
      customPONumber: !!customPONumber ? customPONumber : null,
    });
  });

  const dateInputRenderer = useStableCallback((params) => (
    <CustomDateTextField
      {...params}
      disabled
      size="small"
      InputLabelProps={{ shrink: true }}
    />
  ));

  const handleDateChange = useStableCallback((newValue) => {
    setDateValue(newValue);
  });

  const handleSaveDates = useStableCallback(() => {
    const value = dateValue.startOf("date").format();
    const date = value.split("T")[0];
    onSaveDates(date);
  });

  const handleDeleteDates = useStableCallback(() => {
    onDeleteDates();
  });

  const handleSave = () => {
    onSettingsSave(formData?.selected);
  };

  const handleReset = () => {
    setFormData(INIT_FORM_DATA);
    setGeneratePOBy(GeneratePOByMap[0].id);
    setInputError({ error: false, helperText: "" });
    setCustomPONumber("");
    setDateValue(null);
  };

  const handleFormData = (event) => {
    setFormData((oldData) => ({ ...oldData, selected: event?.target?.value }));
  };

  const disableSaveDates = useMemo(() => {
    return (
      !dateValue ||
      !dayjs(dateValue).isValid() ||
      dateValue <= minValidDate.subtract(1, "day") ||
      dateValue > maxValidDate
    );
  }, [dateValue]);

  return (
    <Box sx={ContentSx}>
      <Box sx={HeaderSx}>
        <Typography fontSize="1.1rem" fontWeight={600}>
          Settings
        </Typography>
        <IconButton aria-label="close" size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={FormSx}>
        <FormControl fullWidth size="small">
          <InputLabel id="selected-dropdown">Selected</InputLabel>
          <Select
            labelId="selected-dropdown"
            label="Selected"
            name="selected"
            value={formData?.selected}
            onChange={handleFormData}
            disabled={hasXPog || isXDepartment}
          >
            <MenuItem dense value="">
              <em>None</em>
            </MenuItem>
            {selectionOptions.map((option) => (
              <MenuItem dense key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={FooterSx}>
        <Button
          variant="outlined"
          sx={{ mr: 1 }}
          onClick={handleReset}
          disabled={hasXPog || isXDepartment}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={hasXPog || isXDepartment}
        >
          Save
        </Button>
      </Box>
      {hasXPog && selectedCount > 1 && (
        <div
          style={{
            margin: "8px 0px 0px 5px",
          }}
        >
          <Typography
            fontSize="0.8rem"
            fontWeight={500}
            sx={{ color: (theme) => theme.palette.grey[600] }}
          >
            Note: Item List Planogram (ITEM99999) is selected, hence you can not
            update the selection.
          </Typography>
        </div>
      )}
      <Divider sx={DividerSx} />
      <Grid container rowSpacing={2} columnSpacing={1}>
        <Grid item xs={6}>
          <FormControl fullWidth size="small">
            <InputLabel id="generate-po-by">Generate PO By</InputLabel>
            <Select
              labelId="generate-po-by"
              label="Generate PO By"
              name="generatePOBy"
              value={generatePOBy}
              onChange={handleGeneratePOByChange}
            >
              {GeneratePOByMap.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {generatePOBy === GeneratePOByMap[2].id && (
          <Grid item xs={6}>
            <TextField
              required
              size="small"
              name="customPONumber"
              value={customPONumber}
              label="Custom PO Number"
              error={inputError.error}
              inputProps={PONumberInputProps}
              onChange={handleCustomPONumberInput}
              helperText={inputError.error && inputError.helperText}
              autoComplete="off"
            />
          </Grid>
        )}
        <Grid item xs={6}>
          <Button
            variant="contained"
            onClick={handleCreatePONumbers}
            disabled={CreatePONumberDisabled}
          >
            Create PO Numbers
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={1} mt={1}>
        <Grid item xs>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Release Date"
              renderInput={dateInputRenderer}
              OpenPickerButtonProps={DatePickerProps}
              minDate={minValidDate}
              maxDate={maxValidDate}
              value={dateValue}
              onChange={handleDateChange}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs="auto">
          <Button
            variant="contained"
            onClick={handleSaveDates}
            disabled={disableSaveDates}
            sx={ButtonSx}
          >
            Save Dates
          </Button>
          <Button variant="contained" onClick={handleDeleteDates}>
            Delete Dates
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default SettingsForm;
